import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Phone,
  Mail,
  MessageSquare,
  Plus,
  Trash2,
  Send,
  Zap,
} from 'lucide-react'
import { MentionMultiSelect } from './MentionMultiSelect'
import { PushAssigneeField } from './PushAssigneeField'
import {
  DEFAULT_TRANSFER_MESSAGE,
  directoryAssigneeRefKey as assigneeRefKey,
  parseDirectoryAssigneeRef as parseAssigneeRef,
} from '@super/shared-types'
import { useDirectory } from '@/contexts/directory.context'
import { parseTargets } from '@/lib/handoff-mentions'
import {
  SearchDropdown,
  type SearchDropdownItem,
} from '@/components/ui/search-dropdown'
import {
  coerceFieldValue,
  findPushOption,
  conversationDescriptionDefaultsFor,
  pushOptionMatchesTarget,
} from '@/lib/handoff-rule.converters'
import { ConversationDescriptionOptionsFields } from './ConversationDescriptionOptionsFields'
import type {
  ConversationDescriptionOptions,
  TransferActionParameters,
} from '@super/shared-types'

// Settings of a converted group's TRANSFER action. Read only in the editor.
export type TransferSettings = Omit<TransferActionParameters, 'recipients'>

export type ActionRowType =
  | 'PHONE_CALL'
  | 'TRANSFER'
  | 'EMAIL'
  | 'SMS'
  | 'PUSH_INTEGRATION'

export type ActionSubtype =
  | 'WORK_ORDER'
  | 'SERVICE_REQUEST'
  | 'GUEST_CARD'
  | 'TASK'

/**
 * One row in the Push picker. Matches the existing handoff form's
 * flattened shape: each (integration × subtype) pair is its own option.
 */
export interface PushOption {
  id: string
  integrationId: string
  integrationType: string
  integrationName: string
  actionSubtype: ActionSubtype | null
  label: string
  iconUrl?: string
  fields?: PushField[]
  defaultParameters?: Record<string, unknown>
}

export interface PushField {
  key: string
  label: string
  inputType: 'dropdown' | 'text'
  dropdownSource?: string
  description?: string
  placeholder?: string
  valueType?: 'numeric' | 'text'
  required?: boolean
  // Resolved at dispatch from a picked staff/role or the action group's linked
  // directory contact, so this field renders the directory selector instead of
  // a plain input.
  directoryAssignee?: boolean
  dependsOnField?: string // key of the field this dropdown cascades from (e.g. stage ← pipeline)
}

export type PushFieldDropdownItem = {
  id: string
  label: string
  sublabel?: string
  groupId?: string // parent field value this item belongs to (e.g. a stage's pipeline id)
}
export type PushFieldDropdownItems = Record<string, PushFieldDropdownItem[]>

export interface PushTarget {
  integrationId: string
  actionSubtype: ActionSubtype | null
  parameters?: Record<string, unknown>
}

export interface ActionRow {
  id: string
  type: ActionRowType
  target: string
  pushTargets?: PushTarget[]
}

export interface ActionGroup {
  id: string
  name: string
  when: string
  actions: ActionRow[]
  transfer?: TransferSettings
}

interface ActionEditorProps {
  value: ActionGroup[]
  onChange: (next: ActionGroup[]) => void
  pushOptions?: PushOption[]
  pushFieldDropdownItems?: PushFieldDropdownItems
  pushFieldFailedSources?: Set<string>
  criteriaErrorGroupIds?: Set<string>
  emptyTargetRowIds?: Set<string>
}

const newId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `id-${Math.random().toString(36).slice(2, 10)}`

const blankAction = (type: ActionRowType = 'PHONE_CALL'): ActionRow => ({
  id: newId(),
  type,
  target: '',
})

/** Baseline transfer settings for a brand-new handoff action group. Setting
 *  this (not undefined) is what tells `editorToWorkflowGroups` to emit a
 *  TRANSFER ApiAction on save; the API then expands it into the per-recipient
 *  PHONE_CALL rows. */
export const DEFAULT_TRANSFER_SETTINGS: TransferSettings = {
  connectionMethod: 'SIMPLE_TRANSFER',
  preserveOriginalCallerId: false,
  declinedMessage: null,
  transferMessage: DEFAULT_TRANSFER_MESSAGE,
  voicemailDetectionEnabled: true,
  leaveVoicemailEnabled: true,
  voicemailMessage: null,
}

const blankActionGroup = (): ActionGroup => ({
  id: newId(),
  name: '',
  when: '',
  actions: [blankAction()],
  transfer: { ...DEFAULT_TRANSFER_SETTINGS },
})

const ALL_ACTION_TYPES: {
  value: ActionRowType
  label: string
  icon: React.ReactNode
}[] = [
  {
    value: 'PHONE_CALL',
    label: 'Transfer call',
    icon: <Phone className="h-4 w-4" />,
  },
  {
    value: 'TRANSFER',
    label: 'Transfer call',
    icon: <Phone className="h-4 w-4" />,
  },
  {
    value: 'EMAIL',
    label: 'Notify via email',
    icon: <Mail className="h-4 w-4" />,
  },
  {
    value: 'SMS',
    label: 'Notify via SMS',
    icon: <MessageSquare className="h-4 w-4" />,
  },
  {
    value: 'PUSH_INTEGRATION',
    label: 'Push',
    icon: <Send className="h-4 w-4" />,
  },
]

export function ActionEditor({
  value,
  onChange,
  pushOptions = [],
  pushFieldDropdownItems = {},
  pushFieldFailedSources = new Set(),
  criteriaErrorGroupIds = new Set(),
  emptyTargetRowIds = new Set(),
}: ActionEditorProps) {
  const actionTypes = ALL_ACTION_TYPES
  const criteriaRequired = value.length > 1

  const updateActionGroup = (id: string, patch: Partial<ActionGroup>) =>
    onChange(
      value.map(group => (group.id === id ? { ...group, ...patch } : group)),
    )
  const removeActionGroup = (id: string) =>
    onChange(value.filter(group => group.id !== id))
  const addActionGroup = () => onChange([...value, blankActionGroup()])

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {criteriaRequired ? (
          <>
            With multiple groups, each one needs{' '}
            <strong>Selection criteria</strong> so the best match can be picked
            at runtime.
          </>
        ) : (
          <>A single group runs unconditionally on every handoff.</>
        )}
      </p>

      {value.length === 0 && (
        <div className="border-2 border-dashed rounded-lg p-8 text-center text-sm text-muted-foreground">
          No groups yet. Add one to define what should happen on a handoff.
        </div>
      )}

      <div className="space-y-3">
        {value.map((group, idx) => (
          <ActionGroupEditor
            key={group.id}
            index={idx}
            group={group}
            actionTypes={actionTypes}
            pushOptions={pushOptions}
            pushFieldDropdownItems={pushFieldDropdownItems}
            pushFieldFailedSources={pushFieldFailedSources}
            onChange={patch => updateActionGroup(group.id, patch)}
            onRemove={() => removeActionGroup(group.id)}
            canRemove={value.length > 1}
            criteriaRequired={criteriaRequired}
            criteriaError={criteriaErrorGroupIds.has(group.id)}
            emptyTargetRowIds={emptyTargetRowIds}
          />
        ))}
      </div>

      <Button type="button" variant="outline" onClick={addActionGroup}>
        <Plus className="h-4 w-4 mr-1" />
        Add group
      </Button>
    </div>
  )
}

interface ActionGroupEditorProps {
  index: number
  group: ActionGroup
  actionTypes: typeof ALL_ACTION_TYPES
  pushOptions: PushOption[]
  pushFieldDropdownItems: PushFieldDropdownItems
  pushFieldFailedSources: Set<string>
  onChange: (patch: Partial<ActionGroup>) => void
  onRemove: () => void
  canRemove: boolean
  criteriaRequired: boolean
  criteriaError: boolean
  emptyTargetRowIds: Set<string>
}

function ActionGroupEditor({
  index,
  group,
  actionTypes,
  pushOptions,
  pushFieldDropdownItems,
  pushFieldFailedSources,
  onChange,
  onRemove,
  canRemove,
  criteriaRequired,
  criteriaError,
  emptyTargetRowIds,
}: ActionGroupEditorProps) {
  // A converted group transfers with its TRANSFER action; other groups with PHONE_CALL rows.
  const hiddenType = group.transfer ? 'PHONE_CALL' : 'TRANSFER'
  const groupActionTypes = actionTypes.filter(
    actionType => actionType.value !== hiddenType,
  )
  const usedTypes = new Set(group.actions.map(action => action.type))
  const allTypes = groupActionTypes.map(actionType => actionType.value)
  const unusedTypes = allTypes.filter(actionType => !usedTypes.has(actionType))
  const canAddAction = unusedTypes.length > 0

  const updateAction = (id: string, patch: Partial<ActionRow>) =>
    onChange({
      actions: group.actions.map(action =>
        action.id === id ? { ...action, ...patch } : action,
      ),
    })
  const removeAction = (id: string) =>
    onChange({ actions: group.actions.filter(action => action.id !== id) })
  const addAction = () => {
    if (!canAddAction) return
    onChange({
      actions: [...group.actions, blankAction(unusedTypes[0])],
    })
  }

  return (
    <div className="border rounded-lg p-4 space-y-3 bg-card">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase text-muted-foreground tracking-wide">
          Group {index + 1}
        </span>
        {canRemove && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRemove}
            className="text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="space-y-2">
        <Label className="text-xs">Name (optional)</Label>
        <Input
          value={group.name}
          onChange={e => onChange({ name: e.target.value })}
          placeholder="Always forward the call"
        />
      </div>

      {criteriaRequired && (
        <div className="space-y-2">
          <Label className="text-xs">Selection criteria</Label>
          <Textarea
            value={group.when}
            onChange={e => onChange({ when: e.target.value })}
            placeholder="e.g. caller is asking about a lockout."
            rows={2}
            className={`resize-none ${
              criteriaError
                ? 'border-destructive focus-visible:ring-destructive'
                : ''
            }`}
          />
          {criteriaError && (
            <p className="text-xs text-destructive">
              Selection criteria is required when there is more than one group.
            </p>
          )}
        </div>
      )}

      <div className="space-y-2">
        <div className="space-y-2">
          {group.actions.map((action, i) => (
            <ActionRowEditor
              actionGroupId={group.id}
              key={action.id}
              row={action}
              index={i}
              actionTypes={groupActionTypes}
              pushOptions={pushOptions}
              pushFieldDropdownItems={pushFieldDropdownItems}
              pushFieldFailedSources={pushFieldFailedSources}
              onChange={patch => updateAction(action.id, patch)}
              onRemove={() => removeAction(action.id)}
              canRemove={group.actions.length > 1}
              transfer={group.transfer}
              disallowedTypes={
                new Set(
                  group.actions
                    .filter(other => other.id !== action.id)
                    .map(other => other.type),
                )
              }
              targetError={emptyTargetRowIds.has(action.id)}
            />
          ))}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addAction}
          disabled={!canAddAction}
          title={
            canAddAction
              ? undefined
              : 'A group can have at most one of each action type.'
          }
        >
          <Plus className="h-4 w-4 mr-1" />
          Add action
        </Button>
      </div>
    </div>
  )
}

export interface ActionRowEditorProps {
  row: ActionRow
  index: number
  actionGroupId: string
  actionTypes: typeof ALL_ACTION_TYPES
  pushOptions: PushOption[]
  pushFieldDropdownItems: PushFieldDropdownItems
  pushFieldFailedSources: Set<string>
  onChange: (patch: Partial<ActionRow>) => void
  onRemove: () => void
  canRemove: boolean
  disallowedTypes: Set<ActionRowType>
  targetError: boolean
  transfer?: TransferSettings
  /** Narrow-container layout: the top line carries index + type + remove,
   *  the target editor flows below full-width. Keeps the row readable in
   *  the node-agent inspector's ~340px aside instead of forcing a horizontal
   *  scroll. Omitted on the wide Actions page where everything fits on one
   *  line. */
  isPanel?: boolean
  /** Show the "N." index gutter. The inspector hides it when there's only
   *  one row because the number adds visual noise with no information. */
  showIndex?: boolean
}

export const ACTION_ROW_TYPE_OPTIONS = ALL_ACTION_TYPES

export function ActionRowEditor({
  row,
  index,
  actionGroupId,
  actionTypes,
  pushOptions,
  pushFieldDropdownItems,
  pushFieldFailedSources,
  onChange,
  onRemove,
  canRemove,
  disallowedTypes,
  targetError,
  transfer,
  isPanel = false,
  showIndex = true,
}: ActionRowEditorProps) {
  const isPush = row.type === 'PUSH_INTEGRATION'

  // Switching type clears irrelevant fields so we don't carry stale data.
  const handleTypeChange = (next: ActionRowType) => {
    if (next === 'PUSH_INTEGRATION') {
      onChange({ type: next, target: '' })
    } else {
      onChange({ type: next, pushTargets: undefined })
    }
  }

  const typeSelect = (
    <Select
      value={row.type}
      onValueChange={next => handleTypeChange(next as ActionRowType)}
    >
      <SelectTrigger className={isPanel ? 'flex-1 min-w-0' : 'w-[175px]'}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {actionTypes
          .filter(
            actionType =>
              actionType.value === row.type ||
              !disallowedTypes.has(actionType.value),
          )
          .map(actionType => (
            <SelectItem key={actionType.value} value={actionType.value}>
              <span className="flex items-center gap-2">
                {actionType.icon}
                {actionType.label}
              </span>
            </SelectItem>
          ))}
      </SelectContent>
    </Select>
  )

  const targetEditor = isPush ? (
    <PushTargetEditor
      row={row}
      actionGroupId={actionGroupId}
      pushOptions={pushOptions}
      pushFieldDropdownItems={pushFieldDropdownItems}
      pushFieldFailedSources={pushFieldFailedSources}
      onChange={onChange}
      targetError={targetError}
    />
  ) : (
    <MentionTargetEditor
      row={row}
      onChange={onChange}
      targetError={targetError}
      transfer={row.type === 'TRANSFER' ? transfer : undefined}
    />
  )

  const removeButton = canRemove ? (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      onClick={onRemove}
      className="text-muted-foreground hover:text-destructive"
    >
      <Trash2 className="h-4 w-4" />
    </Button>
  ) : null

  if (isPanel) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          {showIndex && (
            <div className="flex-shrink-0 text-xs text-muted-foreground tabular-nums w-5 text-right">
              {index + 1}.
            </div>
          )}
          {typeSelect}
          {removeButton}
        </div>
        <div className={showIndex ? 'pl-7' : ''}>{targetEditor}</div>
      </div>
    )
  }

  return (
    <div className="flex items-start gap-2">
      {showIndex && (
        <div className="flex-shrink-0 pt-2 text-xs text-muted-foreground tabular-nums w-6 text-right">
          {index + 1}.
        </div>
      )}
      {typeSelect}
      {targetEditor}
      {removeButton}
    </div>
  )
}

// ─── Mention-based target (Transfer / SMS / Email) ───────────────────

interface MentionTargetEditorProps {
  row: ActionRow
  onChange: (patch: Partial<ActionRow>) => void
  targetError: boolean
  transfer?: TransferSettings
}

function MentionTargetEditor({
  row,
  onChange,
  targetError,
  transfer,
}: MentionTargetEditorProps) {
  const { roles, staff } = useDirectory()

  const placeholder =
    row.type === 'EMAIL'
      ? 'Type @ to pick from directory or paste an email'
      : 'Type @ to pick from directory or paste a phone number'

  const channel = row.type === 'EMAIL' ? 'email' : 'phone'
  const verb =
    row.type === 'EMAIL'
      ? 'Emailing'
      : row.type === 'SMS'
        ? 'Texting'
        : 'Calling'

  const targets = parseTargets(row.target, channel, roles, staff)
  const knownTargets = targets.filter(target => target.kind !== 'unknown')
  const unknownTargets = targets.filter(target => target.kind === 'unknown')

  // Direct staff targets win — when a role expands to its members, drop
  // anyone already picked individually so they're not double-counted.
  const directStaffIds = new Set(
    knownTargets
      .filter(target => target.kind === 'staff')
      .map(target => (target.kind === 'staff' ? target.staffId : '')),
  )
  // Count distinct people by address — someone in two mentioned roles (or a
  // role plus a literal, or listed twice in a dial order) is one person.
  const addressOf = (member: { email: string | null; phone: string | null }) =>
    channel === 'email' ? member.email : member.phone
  const reached = new Set<string>()
  // ROTATION roles don't fan out — the on-call calendar picks one person per
  // day — so each counts as a single recipient instead of all its members.
  let rotationRoles = 0
  for (const target of knownTargets) {
    if (target.kind === 'role') {
      const role = roles.find(({ id }) => id === target.roleId)
      if (!role) continue
      if (role.schedulingMode === 'ROTATION') {
        rotationRoles += 1
        continue
      }
      for (const member of role.members) {
        if (directStaffIds.has(member.staff.id)) continue
        const address = addressOf(member.staff)
        if (address) reached.add(address)
      }
    } else if (target.kind === 'staff') {
      const staffMember = staff.find(({ id }) => id === target.staffId)
      const address = staffMember && addressOf(staffMember)
      if (address) reached.add(address)
    } else {
      reached.add(target.value)
    }
  }
  const peopleReached = reached.size + rotationRoles

  return (
    <div className="flex-1 min-w-0 space-y-1">
      <MentionMultiSelect
        value={row.target}
        onChange={target => onChange({ target })}
        placeholder={placeholder}
        channel={channel}
        allowRepeats={row.type === 'PHONE_CALL' || row.type === 'TRANSFER'}
        hasError={targetError}
      />
      {targetError && (
        <p className="text-xs text-destructive">Add at least one recipient.</p>
      )}
      {!targetError && knownTargets.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {verb} {peopleReached} {peopleReached === 1 ? 'person' : 'people'}
        </p>
      )}
      {unknownTargets.length > 0 && (
        <p className="text-xs text-amber-600">
          ⚠ {unknownTargets.length}{' '}
          {unknownTargets.length === 1 ? 'target' : 'targets'} won't link —
          check the highlighted {unknownTargets.length === 1 ? 'pill' : 'pills'}
          .
        </p>
      )}
      {transfer && <TransferSettingsSummary transfer={transfer} />}
    </div>
  )
}

// Only the settings the method uses: caller ID for a simple transfer, the
// declined message for an AI phone tree transfer.
function TransferSettingsSummary({ transfer }: { transfer: TransferSettings }) {
  const isAiTransfer = transfer.connectionMethod === 'AI_TRANSFER'
  const detail = isAiTransfer
    ? transfer.declinedMessage &&
      `Declined message: “${transfer.declinedMessage}”`
    : transfer.preserveOriginalCallerId
      ? "Shows the caller's number"
      : 'Shows the agent number'
  return (
    <p className="text-xs text-muted-foreground">
      {isAiTransfer ? 'AI phone tree transfer' : 'Simple transfer'}
      {detail && ` · ${detail}`} · Read only
    </p>
  )
}

// ─── Push integration target (integration + subtype) ─────────────────

interface PushTargetEditorProps {
  row: ActionRow
  actionGroupId: string
  pushOptions: PushOption[]
  pushFieldDropdownItems: PushFieldDropdownItems
  pushFieldFailedSources: Set<string>
  onChange: (patch: Partial<ActionRow>) => void
  targetError: boolean
}

function PushTargetEditor({
  row,
  actionGroupId,
  pushOptions,
  pushFieldDropdownItems,
  pushFieldFailedSources,
  onChange,
  targetError,
}: PushTargetEditorProps) {
  const items: SearchDropdownItem<PushOption>[] = pushOptions.map(option => {
    const icon = option.iconUrl ? (
      <img
        src={option.iconUrl}
        alt={option.integrationName}
        className="h-4 w-4 object-contain"
      />
    ) : (
      <Zap className="h-4 w-4" />
    )
    return {
      id: option.id,
      label: option.label,
      sublabel: 'Push Events',
      icon,
      data: option,
    }
  })

  const pushTargets = row.pushTargets ?? []
  const selectedItems: SearchDropdownItem<PushOption>[] = pushTargets
    .map((pt): SearchDropdownItem<PushOption> | null => {
      const opt = findPushOption(pushOptions, pt)
      if (!opt) return null
      const icon = opt.iconUrl ? (
        <img
          src={opt.iconUrl}
          alt={opt.integrationName}
          className="h-4 w-4 object-contain"
        />
      ) : (
        <Zap className="h-4 w-4" />
      )
      return {
        id: opt.id,
        label: opt.label,
        sublabel: 'Push Events',
        icon,
        data: opt,
      }
    })
    .filter((x): x is SearchDropdownItem<PushOption> => x !== null)

  const handleSelect = (
    next:
      | SearchDropdownItem<PushOption>
      | SearchDropdownItem<PushOption>[]
      | null,
  ) => {
    const arr = Array.isArray(next) ? next : next ? [next] : []
    // Preserve parameters on already-picked pushes; new picks adopt the
    // option's defaultParameters (variant id, metadata-derived defaults).
    onChange({
      pushTargets: arr.map(item => {
        const opt = item.data!
        const existing = pushTargets.find(pt =>
          pushOptionMatchesTarget(opt, pt),
        )
        return {
          integrationId: opt.integrationId,
          actionSubtype: opt.actionSubtype,
          parameters:
            existing?.parameters ??
            (opt.defaultParameters ? { ...opt.defaultParameters } : undefined),
        }
      }),
    })
  }

  const updateTargetParam = (
    target: PushTarget,
    key: string,
    value: unknown,
  ) => {
    updateTargetParams(target, { [key]: value })
  }

  // Apply several parameter changes in one update. Used by cascading fields so
  // setting a parent (e.g. pipeline) and clearing its dependents (e.g. stage)
  // happens in a single state write instead of two racing closures.
  const updateTargetParams = (
    target: PushTarget,
    patch: Record<string, unknown>,
  ) => {
    onChange({
      pushTargets: pushTargets.map(pt =>
        pt === target
          ? { ...pt, parameters: { ...(pt.parameters ?? {}), ...patch } }
          : pt,
      ),
    })
  }

  const noIntegrations = pushOptions.length === 0

  return (
    <div className="flex-1 min-w-0 space-y-2">
      <SearchDropdown
        items={items}
        selectedItems={selectedItems}
        onSelect={handleSelect}
        placeholder={
          noIntegrations
            ? 'No push integrations connected'
            : 'Pick integrations to push to'
        }
        searchPlaceholder="Search integrations..."
        multiSelect
        buttonClassName={
          targetError && !noIntegrations
            ? 'w-full border-destructive'
            : 'w-full'
        }
        disabled={noIntegrations}
      />
      {noIntegrations ? (
        <p className="text-xs text-amber-600">
          ⚠ No push-enabled integrations are connected to this company. Connect
          one in Settings → Integrations to use this action.
        </p>
      ) : pushTargets.length > 0 ? (
        <p className="text-xs text-muted-foreground">
          Pushing to {pushTargets.length}{' '}
          {pushTargets.length === 1 ? 'integration' : 'integrations'}
        </p>
      ) : (
        <p
          className={`text-xs ${targetError ? 'text-destructive' : 'text-amber-600'}`}
        >
          {targetError
            ? 'Pick at least one integration.'
            : '⚠ Pick at least one integration to enable this action.'}
        </p>
      )}

      {pushTargets.map((pt, idx) => {
        const opt = findPushOption(pushOptions, pt)
        if (!opt) return null
        // A target with a registry `description` config gets the include
        // dropdown below its fields; conversationDescriptionDefaultsFor is the
        // single source of that decision.
        const descriptionDefaults = conversationDescriptionDefaultsFor(
          opt.integrationType,
          pt.actionSubtype,
        )
        if (!opt.fields?.length && !descriptionDefaults) return null
        return (
          <div
            key={`${pt.integrationId}:${pt.actionSubtype}:${pt.parameters?.taskVariant ?? ''}:${idx}`}
            className="rounded-md border bg-muted/30 p-3 space-y-2"
          >
            <p className="text-xs font-medium">{opt.label}</p>
            {(opt.fields?.length ?? 0) > 0 && (
              <div className="grid grid-cols-2 gap-2">
                {opt.fields!.map(field => {
                  // The directory-backed assignee renders its own selector
                  // (staff/role search, manual override, resolution hint) and
                  // never takes part in dropdown cascading — it's a text field
                  // with a directory reference stored alongside it.
                  if (field.directoryAssignee && opt.integrationType) {
                    return (
                      <PushAssigneeField
                        key={field.key}
                        label={field.label}
                        description={field.description}
                        placeholder={field.placeholder}
                        value={
                          pt.parameters?.[field.key] != null
                            ? String(pt.parameters[field.key])
                            : ''
                        }
                        assigneeRef={parseAssigneeRef(
                          pt.parameters?.[assigneeRefKey(field.key)],
                        )}
                        integrationId={opt.integrationId}
                        integrationType={opt.integrationType}
                        integrationName={opt.integrationName}
                        actionGroupId={actionGroupId}
                        onChange={value =>
                          updateTargetParam(pt, field.key, value || undefined)
                        }
                        onRefChange={ref =>
                          updateTargetParams(pt, {
                            [assigneeRefKey(field.key)]: ref,
                            [field.key]: undefined,
                          })
                        }
                      />
                    )
                  }
                  const rawItems = field.dropdownSource
                    ? pushFieldDropdownItems[field.dropdownSource]
                    : undefined
                  // Cascading dropdown: only show items whose groupId matches the
                  // current value of the field this one depends on (e.g. stages
                  // for the selected pipeline). Empty until the parent is chosen.
                  const dropdownItems = field.dependsOnField
                    ? (rawItems ?? []).filter(item => {
                        const parentValue =
                          pt.parameters?.[field.dependsOnField!]
                        return (
                          item.groupId != null &&
                          parentValue != null &&
                          String(item.groupId) === String(parentValue)
                        )
                      })
                    : rawItems
                  // Fields that cascade from this one — cleared when it changes.
                  const dependents = (opt.fields ?? []).filter(
                    f => f.dependsOnField === field.key,
                  )
                  return (
                    <PushFieldInput
                      key={field.key}
                      field={field}
                      value={pt.parameters?.[field.key]}
                      dropdownItems={dropdownItems}
                      failed={
                        !!field.dropdownSource &&
                        pushFieldFailedSources.has(field.dropdownSource)
                      }
                      onChange={value => {
                        // Re-selecting the current value must not clear
                        // dependents (the dropdown re-fires on same-item click).
                        const unchanged =
                          String(value ?? '') ===
                          String(pt.parameters?.[field.key] ?? '')
                        if (dependents.length > 0 && !unchanged) {
                          const patch: Record<string, unknown> = {
                            [field.key]: value,
                          }
                          for (const dep of dependents)
                            patch[dep.key] = undefined
                          updateTargetParams(pt, patch)
                        } else {
                          updateTargetParam(pt, field.key, value)
                        }
                      }}
                    />
                  )
                })}
              </div>
            )}
            {descriptionDefaults && (
              <ConversationDescriptionOptionsFields
                defaults={descriptionDefaults}
                value={
                  pt.parameters?.conversationDescriptionOptions as
                    | ConversationDescriptionOptions
                    | undefined
                }
                onChange={next =>
                  updateTargetParam(pt, 'conversationDescriptionOptions', next)
                }
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

// ─── Push integration field input (dropdown OR text) ─────────────────

interface PushFieldInputProps {
  field: PushField
  value: unknown
  dropdownItems: PushFieldDropdownItem[] | undefined
  failed: boolean
  onChange: (value: unknown) => void
}

export function PushFieldInput({
  field,
  value,
  dropdownItems,
  failed,
  onChange,
}: PushFieldInputProps) {
  const useDropdown =
    field.inputType === 'dropdown' && !!field.dropdownSource && !failed

  if (useDropdown) {
    const items: SearchDropdownItem<string>[] = (dropdownItems ?? []).map(
      i => ({ id: i.id, label: i.label, sublabel: i.sublabel, data: i.id }),
    )
    const stringValue =
      value !== undefined && value !== null ? String(value) : ''
    const selectedItem = stringValue
      ? (items.find(item => item.id === stringValue) ?? {
          id: stringValue,
          label: `ID: ${stringValue}`,
          data: stringValue,
        })
      : null

    return (
      <div className="space-y-1">
        <Label className="text-xs">{field.label}</Label>
        <SearchDropdown<string>
          items={items}
          selectedItems={selectedItem}
          onSelect={item => {
            const selected = item as SearchDropdownItem<string> | null
            onChange(coerceFieldValue(selected?.data ?? '', field.valueType))
          }}
          buttonLabel={
            selectedItem?.label ?? `Select ${field.label.toLowerCase()}…`
          }
          placeholder={`Select ${field.label.toLowerCase()}…`}
          searchPlaceholder={`Search ${field.label.toLowerCase()}…`}
          emptyMessage={`No ${field.label.toLowerCase()} options found`}
          buttonClassName="w-full [&_.truncate]:ml-0"
        />
        {field.description && (
          <p className="text-xs text-muted-foreground">{field.description}</p>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-1">
      <Label className="text-xs">{field.label}</Label>
      <Input
        placeholder={field.placeholder ?? 'e.g. 12345'}
        value={value !== undefined && value !== null ? String(value) : ''}
        onChange={e =>
          onChange(coerceFieldValue(e.target.value, field.valueType))
        }
      />
      {field.description && (
        <p className="text-xs text-muted-foreground">{field.description}</p>
      )}
    </div>
  )
}
